// /src/components/Sidebar.jsx
import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { 
  LogOut, 
  Book, 
  Plus, 
  Users, 
  Settings, 
  Home,
  User,
  Shield,
  Crown
} from 'lucide-react'
import { useAuth } from '../utils/authContext'

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const getRoleIcon = () => {
    if (isSuperAdmin()) return <Crown className="w-4 h-4 text-yellow-500" />
    if (isAdmin()) return <Shield className="w-4 h-4 text-blue-500" />
    return <User className="w-4 h-4 text-green-500" />
  }

  const getRoleName = () => {
    if (isSuperAdmin()) return 'Super Admin'
    if (isAdmin()) return 'Admin'
    return 'Viewer'
  }

  const getRoleColor = () => {
    if (isSuperAdmin()) return 'bg-yellow-100 text-yellow-800'
    if (isAdmin()) return 'bg-blue-100 text-blue-800'
    return 'bg-green-100 text-green-800'
  }

  const navigationItems = [
    {
      name: 'Dashboard',
      icon: <Home className="w-5 h-5" />,
      href: isSuperAdmin() ? '/root' : isAdmin() ? '/admin' : '/viewer',
      show: true
    },
    {
      name: 'Plant Management',
      icon: <Book className="w-5 h-5" />,
      href: isSuperAdmin() ? '/root' : isAdmin() ? '/admin' : '/viewer',
      show: true
    },
    {
      name: 'Add Plant',
      icon: <Plus className="w-5 h-5" />,
      href: '/root',
      show: isAdmin() || isSuperAdmin()
    },
    {
      name: 'User Management',
      icon: <Users className="w-5 h-5" />,
      href: '/root',
      show: isSuperAdmin()
    },
    {
      name: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      href: '#',
      show: true
    }
  ]

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full bg-white border-r border-forest-200 z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto
        w-64
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-forest-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
              <Book className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-forest-900">PlantWiki</h1>
              <p className="text-xs text-forest-500">Management System</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-forest-400 hover:text-forest-600"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* User Profile */}
        <div className="p-6 border-b border-forest-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-lg">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-forest-900 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-forest-500 truncate">
                {user?.email || 'user@example.com'}
              </p>
              <div className="flex items-center space-x-1 mt-1">
                {getRoleIcon()}
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${getRoleColor()}`}>
                  {getRoleName()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <div className="space-y-2">
            {navigationItems.filter(item => item.show).map((item) => {
              const isActive = location.pathname === item.href
              return (
                <a
                  key={item.name}
                  href={item.href}
                  className={`
                    flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${isActive 
                      ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500' 
                      : 'text-forest-600 hover:text-forest-900 hover:bg-forest-50'
                    }
                  `}
                >
                  {item.icon}
                  <span>{item.name}</span>
                </a>
              )
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-forest-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  )
}

export default Sidebar 